import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CircleNotch, Warning, CheckCircle, XCircle, MinusCircle } from "@phosphor-icons/react";
import SystemDiagram from "../components/home/SystemDiagram";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

const modules = [
  { name: "Sensing Node", desc: "Multi-modal particulate sensing unit. Deployed at ingress-critical locations. Measures mass concentration, particle size distribution, and deposition rate." },
  { name: "Witness / Deposition Element", desc: "Passive collection surface for post-mission analysis. Provides ground-truth correlation for active sensor calibration and long-term exposure assessment." },
  { name: "ControlCore", desc: "Central inference and orchestration unit. Receives node telemetry, correlates with mission state, classifies events, and generates bounded control requests." },
  { name: "Exposure Ledger", desc: "Audit-grade cumulative exposure record. Logs per-volume and per-crew contamination history. Supports compliance reporting and mission-level health tracking." },
];

const operationalStates = [
  { state: "Nominal", color: "#4b7399", icon: <CheckCircle size={18} weight="fill" />, desc: "All sensing nodes reporting within baseline thresholds. No active ingress events." },
  { state: "Ingress Suspected", color: "#d97706", icon: <Warning size={18} weight="fill" />, desc: "One or more nodes report elevated readings. ControlCore correlating with operational state." },
  { state: "Ingress Confirmed", color: "#d97706", icon: <Warning size={18} weight="bold" />, desc: "Event classification complete. Containment response initiated. Crew alerting active." },
  { state: "Clearing In Progress", color: "#4b7399", icon: <CircleNotch size={18} weight="bold" />, desc: "ECLSS or vehicle systems executing clearing protocol. Monitoring particle decay curve." },
  { state: "Clearance Verified", color: "#4b7399", icon: <CheckCircle size={18} weight="regular" />, desc: "All nodes returned to baseline. Event logged. Exposure ledger updated." },
  { state: "Degraded Sensor", color: "#8c9eb3", icon: <XCircle size={18} weight="regular" />, desc: "One or more nodes offline or reporting anomalous data. Fallback coverage active." },
];

const outputs = [
  { label: "Local Alert", desc: "Visual/audible crew notification at the affected zone." },
  { label: "Control Request", desc: "Bounded command to ECLSS or vehicle environmental subsystem." },
  { label: "Contamination Event Log", desc: "Timestamped record of event type, duration, peak levels, and response actions." },
  { label: "Cumulative Exposure Record", desc: "Per-volume and per-crew integrated exposure data for health monitoring." },
];

export default function ProductPage() {
  return (
    <main className="pt-16">
      {/* Header */}
      <section data-testid="product-hero" className="relative py-20 md:py-32 border-b border-[#2d3239] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/kfjvtoua_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%282%29.png"
            alt="DustSentinel-LX habitat monitoring station"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.15]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0b0d] via-[#0a0b0d]/90 to-[#0a0b0d]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Product</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-[#f2f4f8] leading-[1.05] mb-6">
              DustSentinel-LX
            </h1>
            <p className="text-base md:text-lg text-[#8c9eb3] leading-relaxed max-w-2xl mb-4">
              A lunar dust ingress monitoring and containment-control subsystem for habitable volumes. DustSentinel-LX is not an external dust-removal device. It is the interior-side detection, inference, alerting, and exposure-record layer for crewed lunar systems.
            </p>
          </motion.div>
        </div>
      </section>

      {/* System Architecture */}
      <section data-testid="product-architecture" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Architecture</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              System Architecture
            </h2>
          </motion.div>
          <div className="flex justify-center mb-8">
            <SystemDiagram />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-12">
            {[
              "Node A: Airlock Threshold",
              "Node B: Habitat Breathing Zone",
              "Node C: Rover Ingress Zone",
              "ControlCore",
              "ECLSS / Vehicle I/F",
              "Crew UI / Telemetry",
            ].map((item, i) => (
              <motion.div
                key={item}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bg-[#121519] border border-[#2d3239] rounded-none p-4 text-center"
              >
                <p className="font-mono text-xs text-[#8c9eb3]">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section data-testid="product-modules" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Modules</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              System Modules
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {modules.map((mod, i) => (
              <motion.div
                key={mod.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bg-[#121519] border border-[#2d3239] rounded-none p-8 hover:border-[#4b7399] transition-colors duration-300"
              >
                <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-2">Module</p>
                <h3 className="text-xl font-semibold text-[#f2f4f8] mb-3">{mod.name}</h3>
                <p className="text-sm text-[#8c9eb3] leading-relaxed">{mod.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Operational Logic */}
      <section data-testid="product-operational-logic" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Operational Logic</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              System States
            </h2>
          </motion.div>
          <div className="space-y-3">
            {operationalStates.map((item, i) => (
              <motion.div
                key={item.state}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="flex items-start gap-4 bg-[#121519] border border-[#2d3239] rounded-none p-6 hover:border-[#4b7399] transition-colors duration-300"
              >
                <div style={{ color: item.color }} className="mt-0.5 flex-shrink-0">{item.icon}</div>
                <div>
                  <p className="font-mono text-sm font-medium text-[#f2f4f8]">{item.state}</p>
                  <p className="text-sm text-[#8c9eb3] mt-1">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outputs */}
      <section data-testid="product-outputs" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Outputs</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              System Outputs
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {outputs.map((item, i) => (
              <motion.div
                key={item.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="border-l-2 border-[#4b7399] pl-6 py-4"
              >
                <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-1">{item.label}</p>
                <p className="text-sm text-[#8c9eb3]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment Environments */}
      <section data-testid="product-deployment" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">Environments</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              Deployment Environments
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {["Habitat Module", "Airlock Vestibule", "Rover Corridor", "Sample Transfer Bay"].map((env, i) => (
              <motion.div
                key={env}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bg-[#121519] border border-[#2d3239] rounded-none p-6 text-center hover:border-[#4b7399] transition-colors"
              >
                <MinusCircle size={20} className="text-[#4b7399] mx-auto mb-3" />
                <p className="font-mono text-sm text-[#f2f4f8]">{env}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-base text-[#8c9eb3] mb-6">
              Technical overview and architecture sheet available on request.
            </p>
            <Link
              to="/contact"
              data-testid="product-cta-button"
              className="bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-8 py-3 rounded-none hover:bg-[#3a5a78] transition-colors inline-flex items-center gap-2"
            >
              Request Technical Overview <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
